/**
 * Device-local persistence for the auth session.
 *
 * The uid (Cognito IdentityId) lives here under a dedicated key that is
 * written once at guest bootstrap and NEVER overwritten on signup — that is
 * exactly what makes the uid survive the guest -> account upgrade. The wider
 * session (email + tokens) is persisted separately so a page reload keeps the
 * user signed in.
 *
 * INTERIM (workspace tracks hardening): User Pool tokens are stored in
 * localStorage. This is the standard SPA trade-off (Amplify does the same) and
 * is acceptable for the dev MVP; a future hardening can move to in-memory
 * access tokens + refresh. Storage access is guarded for SSR / static export
 * (no `window` during prerender).
 */
import type { PersistedSession } from './types';

const UID_KEY = 'simbay.uid';
const SESSION_KEY = 'simbay.session';

function safeLocalStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    // localStorage can throw (private mode, disabled cookies) — degrade to
    // an in-memory-only session rather than crashing the trainer.
    return null;
  }
}

/** The cached uid (IdentityId), or null on a fresh device. */
export function readIdentityId(): string | null {
  return safeLocalStorage()?.getItem(UID_KEY) ?? null;
}

/**
 * Persist the uid. Writes ONLY when absent — the guest IdentityId is the
 * stable uid and must not be clobbered by a later bootstrap or a signup link.
 */
export function persistIdentityId(identityId: string): void {
  const store = safeLocalStorage();
  if (!store) return;
  if (store.getItem(UID_KEY)) return;
  store.setItem(UID_KEY, identityId);
}

export function readSession(): PersistedSession | null {
  const raw = safeLocalStorage()?.getItem(SESSION_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as PersistedSession;
  } catch {
    return null;
  }
}

export function persistSession(session: PersistedSession): void {
  safeLocalStorage()?.setItem(SESSION_KEY, JSON.stringify(session));
}

/** Clear the authed session (keeps the uid — a signed-out device is still a guest). */
export function clearSession(): void {
  safeLocalStorage()?.removeItem(SESSION_KEY);
}
